import { Popup } from "@vis.gl/react-maplibre";
import { FC, useState } from "react";
import useEvent from "react-use-event-hook";

import {
  DirectionEnum,
  getRealStationMarkerNumber,
  getWaypointCategoryVisual,
  IRideRoute,
  isLastRealRouteLocation,
  NO_VALUE_DISPLAY,
  StationMarkerVariantEnum,
} from "@ryde/shared";

import {
  IRideMapActiveStation,
  MapStationInfoPopup,
  RideMapStationPopupContext,
} from "../../ride-map-station-popup";
import { MIN_LINE_POINTS } from "../route-block-maplibre.constants";
import { MapLibreGeoJsonLine } from "./maplibre-geojson-line";
import { MapLibrePolylineEdit } from "./maplibre-polyline-edit";
import { MapLibreStationDotsLayer } from "./maplibre-station-dots-layer";
import { MapLibreStationMarker } from "./maplibre-station-marker";
import { useMapLibrePlannedRoute } from "./use-maplibre-planned-route";

interface IMapLibrePlannedRouteProps {
  route: IRideRoute;
  direction?: DirectionEnum;
  showPlannedPath?: boolean;
  stationMarkerVariant?: StationMarkerVariantEnum;
  isEditRoutePolylineAllowed?: boolean;
  /** Crowded map — draw stations as GPU circles instead of one HTML marker per stop. */
  isCrowdedMap?: boolean;
}

export const MapLibrePlannedRoute: FC<IMapLibrePlannedRouteProps> = ({
  route,
  direction,
  showPlannedPath = true,
  stationMarkerVariant = StationMarkerVariantEnum.pin,
  isEditRoutePolylineAllowed = false,
  isCrowdedMap = false,
}) => {
  const { rideId, rideStatus, stationRouteId, courseId } =
    route as Required<IRideRoute>;

  const [activeStation, setActiveStation] =
    useState<IRideMapActiveStation | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [addPointCallback, setAddPointCallback] = useState<
    ((lat: number, lng: number) => void) | undefined
  >(undefined);

  const { editorPositions, markerLocations, colorValue, pathFeature } =
    useMapLibrePlannedRoute({
      route,
      showPlannedPath,
      isEditRoutePolylineAllowed,
      direction,
    });

  const openStationPopup = useEvent((station: IRideMapActiveStation) => {
    setActiveStation(station);
  });
  const closeStationPopup = useEvent(() => {
    setActiveStation(null);
  });

  const handleDrawingStateChange = useEvent(
    (
      isDrawingState: boolean,
      addPointHandler?: (lat: number, lng: number) => void,
    ) => {
      setIsDrawing(isDrawingState);
      setAddPointCallback(() => addPointHandler);
    },
  );

  const stationLabel =
    activeStation?.addressName?.trim() ||
    activeStation?.fullAddress?.trim() ||
    NO_VALUE_DISPLAY;
  const routeLayerId = rideId ?? stationRouteId ?? courseId;

  let plannedPathNode = null;
  if (
    showPlannedPath &&
    isEditRoutePolylineAllowed &&
    editorPositions.length >= MIN_LINE_POINTS
  ) {
    plannedPathNode = (
      <MapLibrePolylineEdit
        positions={editorPositions}
        colorValue={colorValue}
        onDrawingStateChange={handleDrawingStateChange}
      />
    );
  } else if (pathFeature) {
    plannedPathNode = (
      <MapLibreGeoJsonLine
        id={`planned-route-${routeLayerId}`}
        feature={pathFeature}
        color={colorValue}
        withArrows
      />
    );
  }

  return (
    <RideMapStationPopupContext.Provider value={{ openStationPopup }}>
      {plannedPathNode}

      {/* Drawing thousands of pin markers is what stalls a crowded map — dots go to the GPU instead. */}
      {isCrowdedMap && !isDrawing ? (
        <MapLibreStationDotsLayer
          id={`stations-${routeLayerId}`}
          rideId={rideId}
          rideStatus={rideStatus}
          locations={markerLocations}
          color={colorValue}
          onOpen={openStationPopup}
        />
      ) : (
        markerLocations.map((locationInfo, waypointIndex) => (
          <MapLibreStationMarker
            key={`way_${locationInfo.latitude}_${locationInfo.longitude}_${waypointIndex}_${locationInfo.waypointCategory ?? ""}`}
            rideId={rideId}
            rideStatus={rideStatus}
            locationInfo={locationInfo}
            waypointIndex={waypointIndex}
            stationMarkerNumber={getRealStationMarkerNumber({
              locations: markerLocations,
              locationIndex: waypointIndex,
            })}
            direction={direction}
            markerBackgroundColor={
              locationInfo.waypointCategory
                ? getWaypointCategoryVisual(locationInfo.waypointCategory).color
                : colorValue
            }
            isLastWayPoint={isLastRealRouteLocation({
              locations: markerLocations,
              locationIndex: waypointIndex,
            })}
            stationMarkerVariant={
              locationInfo.waypointCategory
                ? StationMarkerVariantEnum.waypoint
                : stationMarkerVariant
            }
            isDrawing={isDrawing}
            onOpen={openStationPopup}
            onMarkerClick={addPointCallback}
          />
        ))
      )}

      {activeStation && (
        <Popup
          longitude={activeStation.longitude}
          latitude={activeStation.latitude}
          anchor="bottom"
          offset={20}
          className="maplibre-station-info-popup"
          closeButton={!activeStation.fullPassengersDetails?.length}
          closeOnClick
          onClose={closeStationPopup}
        >
          {activeStation.fullPassengersDetails?.length ? (
            <MapStationInfoPopup
              rideId={activeStation.rideId}
              rideStatus={activeStation.rideStatus}
              stationId={activeStation.stationId}
              addressName={activeStation.addressName || stationLabel}
              fullAddress={activeStation.fullAddress}
              fullPassengersDetails={activeStation.fullPassengersDetails}
            />
          ) : (
            stationLabel
          )}
        </Popup>
      )}
    </RideMapStationPopupContext.Provider>
  );
};
